import React from "react";
import { RoleHelper } from "src/helpers";
import { ValidationErrors } from "src/errors";
import { MAX_USER_ACCOUNT_SUPERVISOR_EMAILS, MAX_VENDORS_PER_ACCOUNT } from "src/constants";
import {
  UserAccount,
  UserRole,
  UpdateUserAccountFormData,
  UpdateUserAccountFormDataKey,
  HealfManagementTeamRole,
  UpdateUserAccountFormAction
} from "src/types";
import { useAuthorisation } from "src/context";
import { useScrollOnMessage } from "src/hooks";
import { UserSupervisorToolTip, SubmissionTooltip } from "src/components/tooltips";
import { DropdownSelector, TextInputBox, TextInputBoxList } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { CollapsibleSection } from "src/components/ui/collapsible";
import { StatusPill } from "src/components/ui";

interface UpdateUserAccountFormProps {
  formData: UpdateUserAccountFormData;
  userAccountToUpdate?: UserAccount;
  vendorsOptions: string[];
  validationErrors?: ValidationErrors<UpdateUserAccountFormDataKey>;
  formLvlErrorMessage: string;
  isFormUpdated: boolean;
  handleInputChange: (
    field: UpdateUserAccountFormDataKey,
    value: UpdateUserAccountFormData[keyof UpdateUserAccountFormData]
  ) => void;
  handleSubmit: (e?: React.FormEvent) => void;
  setAction: (action: UpdateUserAccountFormAction) => void;
}

const UpdateUserAccountForm: React.FC<UpdateUserAccountFormProps> = ({
  formData,
  userAccountToUpdate,
  vendorsOptions,
  validationErrors,
  formLvlErrorMessage,
  isFormUpdated,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const { userRole } = useAuthorisation();
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const userRoleDisplayOptions: UserRole[] = RoleHelper.roleAssignmentOptions[userRole as HealfManagementTeamRole];

  const handleSupervisorEmailsChange: React.Dispatch<React.SetStateAction<string[]>> = (emails) =>
    handleInputChange("supervisorEmails", emails as string[]);

  const handleVendorNamesChange: React.Dispatch<React.SetStateAction<string[]>> = (vendorNames) =>
    handleInputChange("vendorNames", vendorNames as string[]);

  const addEmptySupervisorEmail = () =>
    formData.supervisorEmails.length < MAX_USER_ACCOUNT_SUPERVISOR_EMAILS &&
    handleInputChange("supervisorEmails", [...formData.supervisorEmails, ""]);

  const addEmptyVendorName = () =>
    formData.vendorNames.length < MAX_VENDORS_PER_ACCOUNT &&
    handleInputChange("vendorNames", [...formData.vendorNames, ""]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Update User Account</h1>
            <p className="text-primary-100 mt-2 text-lg">Modify an existing user account</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-8">
              {/* Fetch User Section */}
              <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                <CollapsibleSection
                  initiallyOpen={true}
                  title={
                    <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      Find User Account
                    </h3>
                  }
                >
                  <div className="p-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">User Email</label>
                      <TextInputBox
                        id="user-account-email"
                        onInputChange={(value: string) => handleInputChange("email", value)}
                        inputValue={formData.email}
                      />
                      <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("email")} />
                      <div className="mt-4">
                        <button
                          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                          onClick={() => setAction("FETCH")}
                          type="submit"
                        >
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                            Fetch User
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </CollapsibleSection>
              </div>

              {/* User Details Section */}
              {userAccountToUpdate && (
                <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                  <CollapsibleSection
                    initiallyOpen={true}
                    title={
                      <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
                        <svg
                          className="w-5 h-5 mr-2 text-primary-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        User Information
                      </h3>
                    }
                  >
                    <div className="p-6">
                      {/* Status */}
                      {userAccountToUpdate.status && (
                        <div className="mb-6 flex items-center">
                          <span className="text-sm font-medium text-secondary-700 mr-4">Account Status:</span>
                          <StatusPill status={userAccountToUpdate.status} />
                        </div>
                      )}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Role</label>
                          <DropdownSelector
                            id="user-role"
                            options={userRoleDisplayOptions}
                            onSelect={(option: string) => handleInputChange("role", option as UserRole)}
                            value={formData.role}
                            placeholder="Select a role"
                          />
                          <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("role")} />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Name</label>
                          <TextInputBox
                            id="user-name"
                            onInputChange={(value: string) => handleInputChange("name", value)}
                            inputValue={formData.name}
                          />
                          <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("name")} />
                        </div>

                        {RoleHelper.isHealfOperationsTeam(formData.role) && (
                          <>
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-secondary-700 mb-2">
                                Job Title <span className="text-secondary-500 text-xs">(optional)</span>
                              </label>
                              <TextInputBox
                                id="job-title"
                                onInputChange={(value: string) => handleInputChange("jobTitle", value)}
                                inputValue={formData.jobTitle}
                              />
                              <ValidationErrorMessage
                                message={validationErrors?.getErrorMessageByFormDataKey("jobTitle")}
                              />
                            </div>

                            <div className="lg:col-span-2 space-y-2">
                              <label className="flex items-center text-sm font-medium text-secondary-700 mb-2">
                                Supervisor Emails <span className="text-secondary-500 text-xs ml-2">(optional)</span>
                                <div className="ml-2">
                                  <UserSupervisorToolTip />
                                </div>
                              </label>
                              <TextInputBoxList
                                id="supervisor-email"
                                inputValueList={formData.supervisorEmails}
                                onInputChange={handleSupervisorEmailsChange}
                              />
                              <ValidationErrorMessage
                                message={validationErrors?.getErrorMessageByFormDataKey("supervisorEmails")}
                              />
                              <button
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg border border-primary-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={addEmptySupervisorEmail}
                                type="button"
                                disabled={formData.supervisorEmails.length >= MAX_USER_ACCOUNT_SUPERVISOR_EMAILS}
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                  />
                                </svg>
                                Add Supervisor Email
                              </button>
                            </div>
                          </>
                        )}

                        {formData.role === "VENDOR" && (
                          <div className="lg:col-span-2 space-y-2">
                            <label className="block text-sm font-medium text-secondary-700 mb-2">Vendors</label>
                            <TextInputBoxList
                              id="vendor-name"
                              inputValueList={formData.vendorNames}
                              onInputChange={handleVendorNamesChange}
                              dropDownOptions={vendorsOptions.filter((v) => !formData.vendorNames.includes(v))}
                            />
                            <ValidationErrorMessage
                              message={validationErrors?.getErrorMessageByFormDataKey("vendorNames")}
                            />
                            <button
                              className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg border border-primary-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={addEmptyVendorName}
                              type="button"
                              disabled={formData.vendorNames.length >= MAX_VENDORS_PER_ACCOUNT}
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                />
                              </svg>
                              Add Vendor
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CollapsibleSection>
                </div>
              )}

              {/* Actions Section */}
              {userAccountToUpdate && (
                <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                  {/* <CollapsibleSection
                    initiallyOpen={false}
                    title={
                      <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
                        <svg
                          className="w-5 h-5 mr-2 text-primary-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        Account Actions
                      </h3>
                    }
                  > */}
                  <div className="p-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <button
                        className={`px-6 py-3 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          userAccountToUpdate.status === "Deactivated" || isFormUpdated
                            ? "bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:ring-primary-500 shadow-lg hover:shadow-xl"
                            : "bg-secondary-400 cursor-not-allowed"
                        }`}
                        disabled={userAccountToUpdate.status !== "Deactivated" && !isFormUpdated}
                        onClick={() => setAction("UPDATE")}
                        type="submit"
                      >
                        <span className="flex items-center">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {userAccountToUpdate.status === "Deactivated" ? "Reactivate" : "Update Account"}
                        </span>
                      </button>

                      {(userAccountToUpdate.status === "Active" || userAccountToUpdate.status === "Provisioned") && (
                        <button
                          className="px-6 py-3 bg-warning-600 text-white rounded-lg hover:bg-warning-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-warning-500 focus:ring-offset-2"
                          onClick={() =>
                            setAction(
                              userAccountToUpdate.status === "Active"
                                ? "DEACTIVATE_CONFIRMATION"
                                : "DELETE_CONFIRMATION"
                            )
                          }
                          type="submit"
                        >
                          <span className="flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                              />
                            </svg>
                            {userAccountToUpdate.status === "Active" ? "Deactivate" : "Delete"}
                          </span>
                        </button>
                      )}
                    </div>
                    {!isFormUpdated && userAccountToUpdate.status !== "Deactivated" && (
                      <div className="mt-4">
                        <SubmissionTooltip formState={"NO_CHANGE"} />
                      </div>
                    )}
                  </div>
                  {/* </CollapsibleSection> */}
                </div>
              )}
            </div>

            {/* Form Level Error Message */}
            <div className="mt-6">
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { UpdateUserAccountForm };
